package br.com.heyagenda.data.remote

import br.com.heyagenda.domain.model.ProfessionalAvailability
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.FirebaseFirestore
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.map
import kotlinx.serialization.Serializable

@Serializable
data class ProfessionalAvailabilityDto(
    val weeklyScheduleJson: String = "",
    val daysOffJson: String = ""
)

interface FirestoreAvailabilityDataSource {
    fun observeAvailability(uid: String): Flow<ProfessionalAvailability?>
    suspend fun getAvailability(uid: String): ProfessionalAvailability?
    suspend fun saveAvailability(uid: String, availability: ProfessionalAvailability): Result<Unit>
}

class FirestoreAvailabilityDataSourceImpl : FirestoreAvailabilityDataSource {

    private val firestore: FirebaseFirestore? by lazy {
        try {
            Firebase.firestore
        } catch (e: Throwable) {
            println(">>> [FirestoreAvailability ERROR] Failed to get Firebase.firestore: ${e.message}")
            null
        }
    }

    override suspend fun getAvailability(uid: String): ProfessionalAvailability? {
        val fs = firestore ?: return null
        return try {
            val doc = fs.collection("professionals")
                .document(uid)
                .collection("availability")
                .document("schedule")
                .get()
            if (doc.exists) {
                doc.data<ProfessionalAvailability>()
            } else {
                null
            }
        } catch (e: Throwable) {
            println(">>> [FirestoreAvailability GET ERROR] $uid: ${e.message}")
            null
        }
    }

    override fun observeAvailability(uid: String): Flow<ProfessionalAvailability?> = flow {
        val fs = firestore
        if (fs == null) {
            println(">>> [FirestoreAvailability ERROR] firestore is NULL")
            emit(null)
            return@flow
        }

        // 1. Emit direct get result first
        val direct = getAvailability(uid)
        emit(direct)

        // 2. Stream real-time updates safely
        try {
            fs.collection("professionals")
                .document(uid)
                .collection("availability")
                .document("schedule")
                .snapshots
                .map { snapshot ->
                    if (snapshot.exists) {
                        try {
                            snapshot.data<ProfessionalAvailability>()
                        } catch (e: Throwable) {
                            println(">>> [FirestoreAvailability DECODE ERROR] ${e.message}")
                            null
                        }
                    } else {
                        null
                    }
                }
                .catch { e ->
                    println(">>> [FirestoreAvailability SNAPSHOT ERROR] $uid: ${e.message}")
                }
                .collect {
                    emit(it)
                }
        } catch (e: Throwable) {
            println(">>> [FirestoreAvailability SNAPSHOT SETUP ERROR] ${e.message}")
        }
    }

    override suspend fun saveAvailability(uid: String, availability: ProfessionalAvailability): Result<Unit> {
        val fs = firestore ?: return Result.failure(IllegalStateException("Firestore unavailable"))
        return try {
            fs.collection("professionals")
                .document(uid)
                .collection("availability")
                .document("schedule")
                .set(availability)
            println(">>> [FirestoreAvailability SUCCESS] Saved availability schedule for $uid")
            Result.success(Unit)
        } catch (e: Throwable) {
            println(">>> [FirestoreAvailability SAVE ERROR] ${e.message}")
            Result.failure(e)
        }
    }
}
